import React, { useCallback, useEffect, useRef } from "react";
import { Tooltip } from "react-tooltip";
import { useField, useFormikContext } from "formik";
import styled from "styled-components";

import { Button, ButtonTheme, Icon, Label } from "@components";
import { useI18n } from "@i18n";
import { device } from "@styles/breakpoints";

interface Props {
  name?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  tooltip?: string;
  accept?: string;
  maxSizeBytes?: number;
  customDepositFileUrl?: string;
  onRemoveFile?: () => void;
}

const DEFAULT_ACCEPT = ".pdf, .doc, .docx, .xls, .xlsx, txt";
const DEFAULT_MAX = 10 * 1024 * 1024;
const ALLOWED_MIME = ["application/pdf", "application/msword", "image/jpeg"];
const ALLOWED_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", "txt"];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme?.tripleMargin || "24px"};
  margin-top: ${({ theme }) => theme?.smallestMargin || "24px"};
  width: 100%;

  @media ${device.tablet} {
    margin-bottom: 16px;
    margin-top: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;

  @media ${device.mobileL} {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.7;
`;

const DropZone = styled.div<{ $disabled?: boolean }>`
  border: 2px dashed;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: border-color 0.15s ease-in-out, background 0.15s ease-in-out;
  width: 100%;
  box-sizing: border-box;

  @media ${device.tablet} {
    padding: 16px;
    border-radius: 8px;
  }

  @media ${device.mobileL} {
    padding: 12px;
    border-radius: 6px;
  }

  &:focus {
    outline: 2px solid;
  }
`;

const FileName = styled.div`
  margin-bottom: 8px;
  word-break: break-all;
`;

const ErrorText = styled.div`
  color: #dc2626;
  margin-top: 8px;
  font-size: 12px;
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;

  & button {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
  }

  @media ${device.tablet} {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  @media ${device.mobileL} {
    gap: 8px;
  }
`;

const ExistingFileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;
const DepositAttachmentField = ({
  name = "depositAttachment",
  disabled = false,
  required = false,
  label,
  tooltip,
  accept = DEFAULT_ACCEPT,
  maxSizeBytes = DEFAULT_MAX,
  customDepositFileUrl = undefined,
  onRemoveFile,
}: Props) => {
  const { t } = useI18n();

  const inputIdRef = useRef(
    `deposit-attachment-input-${name}-${Math.random().toString(36).slice(2)}`
  );
  const tooltipIdRef = useRef(
    `deposit-attachment-tooltip-${name}-${Math.random().toString(36).slice(2)}`
  );
  const inputId = inputIdRef.current;
  const tooltipId = tooltipIdRef.current;

  const [, meta, helpers] = useField<File | null>(name);
  const { setError, setTouched } = helpers;
  const { setFieldValue, values } = useFormikContext<any>();
  const file: File | null = values?.[name] ?? null;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDialogOpen = useRef(false);

  useEffect(() => {
    const onFocus = () => {
      isDialogOpen.current = false;
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const isAllowedType = useCallback((f: File | null | undefined) => {
    if (!f) return true;
    const typeOk = ALLOWED_MIME.includes(f.type);
    const extOk = ALLOWED_EXT.some((ext) => f.name.toLowerCase().endsWith(ext));
    return typeOk || extOk;
  }, []);

  const finalizeInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current.value = "";
    }
    setTimeout(() => {
      isDialogOpen.current = false;
    }, 0);
  }, []);

  const handlePick = useCallback(
    (f: File | null | undefined) => {
      if (!f) {
        setFieldValue(name, null);
        setError(undefined);
        finalizeInput();
        return;
      }

      if (!isAllowedType(f)) {
        setFieldValue(name, null);
        setTouched(true, true);
        setError("auctionForm.depositAttachment.errors.format");
        finalizeInput();
        return;
      }

      if (f.size > maxSizeBytes) {
        setFieldValue(name, null);
        setTouched(true, true);
        setError("auctionForm.depositAttachment.errors.max50mb");
        finalizeInput();
        return;
      }

      setError(undefined);
      setTouched(true, true);
      setFieldValue(name, f);
      finalizeInput();
    },
    [
      finalizeInput,
      isAllowedType,
      maxSizeBytes,
      name,
      setError,
      setFieldValue,
      setTouched,
    ]
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    if (isDialogOpen.current) return;
    isDialogOpen.current = true;
    inputRef.current?.click();
  }, [disabled]);

  const handleDownload = useCallback(() => {
    if (customDepositFileUrl) {
      const link = document.createElement("a");
      link.href = customDepositFileUrl;
      link.download = "custom-deposit-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [customDepositFileUrl]);

  const handleRemoveExistingFile = useCallback(() => {
    if (onRemoveFile) {
      onRemoveFile();
    }
  }, [onRemoveFile]);

  return (
    <Container>
      <Header>
        <Label htmlFor={inputId}>
          {label ?? String(t("auctionForm.depositAttachment.label"))}
          {required ? " *" : ""}
        </Label>
        <div id={tooltipId} aria-label={String(t("common.info"))}>
          <Icon.InfoCircle aria-hidden />
        </div>
        <Tooltip anchorId={tooltipId}>
          <div style={{ maxWidth: "20rem" }}>
            {tooltip ?? String(t("auctionForm.depositAttachment.tooltip"))}
          </div>
        </Tooltip>
      </Header>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => handlePick(e.currentTarget.files?.[0] ?? null)}
        disabled={disabled}
      />

      <DropZone
        role="button"
        tabIndex={0}
        aria-controls={inputId}
        aria-disabled={disabled}
        $disabled={disabled}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          if (!disabled) e.preventDefault();
        }}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          const dropped = e.dataTransfer.files?.[0];
          handlePick(dropped ?? null);
        }}
      >
        {customDepositFileUrl && customDepositFileUrl.trim() !== "" ? (
          <ExistingFileContainer>
            <div>{String(t("auctionForm.common.existingFileAttached"))}</div>
            <FileActions>
              <Button type="button" onClick={handleDownload}>
                <Icon.Download />
                {String(t("auctionForm.common.download"))}
              </Button>
              <Button
                theme={ButtonTheme.Quaternary}
                type="button"
                onClick={handleRemoveExistingFile}
              >
                <Icon.Times />
                {String(t("auctionForm.common.remove"))}
              </Button>
            </FileActions>
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
              {String(t("auctionForm.common.clickBrowseFilesToAttach"))}
            </div>
            <Button type="button" onClick={openPicker} disabled={disabled}>
              {String(t("auctionForm.common.browseFiles"))}
            </Button>
          </ExistingFileContainer>
        ) : file ? (
          <div>
            <FileName>{file.name}</FileName>
            <Button
              theme={ButtonTheme.Quaternary}
              type="button"
              onClick={() => handlePick(null)}
            >
              {String(t("auctionForm.common.remove"))}
            </Button>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 6 }}>
              {String(t("auctionForm.depositAttachment.dropHere"))}
            </div>
            <Button type="button" onClick={openPicker} disabled={disabled}>
              {String(t("auctionForm.common.browseFiles"))}
            </Button>
            <Hint>{String(t("auctionForm.depositAttachment.hint"))}</Hint>
          </div>
        )}
      </DropZone>

      {meta.touched && meta.error && (
        <ErrorText>{String(t(meta.error as string))}</ErrorText>
      )}
    </Container>
  );
};

export default DepositAttachmentField;
